/**
 * CardEditorErrorBoundary — Catches render errors in the card editor.
 *
 * Also exports CardEditorLoadingGuard which handles loading/error/ready
 * states from useCardEditorState, keeping PageEditorPage.tsx lean.
 */

import { Component, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

// ── Error Boundary (class component — React requirement) ─────────────

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class CardEditorErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-500" />
          <p className="text-sm font-medium text-gray-700">Something went wrong</p>
          <p className="text-xs text-gray-400">The card editor encountered an error.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 flex items-center gap-1.5 rounded-sm border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Loading Guard (wraps card editor sidebar content) ────────────────

interface CardEditorLoadingGuardProps {
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  children: ReactNode
}

export function CardEditorLoadingGuard({
  isLoading,
  isError,
  onRetry,
  children,
}: CardEditorLoadingGuardProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-sm bg-gray-100" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-red-400" />
        <p className="text-sm font-medium text-gray-700">Failed to load card design</p>
        <p className="text-xs text-gray-400">Check your connection and try again.</p>
        <button
          onClick={onRetry}
          className="mt-2 flex items-center gap-1.5 rounded-sm border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try Again
        </button>
      </div>
    )
  }

  return <>{children}</>
}
